<!-- Hire card: the next step, what this hire costs, and the decision. -->
<section class="flex flex-col overflow-hidden rounded-xl border" aria-label="Hire"
    [class]="highlight() ? 'border-(--app-accent-ring) bg-(--app-accent-soft)' : 'border-(--border-subtle) bg-(--bg-alt)'">
    <div class="flex items-center gap-3.5 px-4 pt-3.5 pb-3">
        <span class="grid size-[38px] shrink-0 place-items-center rounded-full bg-(--mba-surface) text-(--app-accent)" aria-hidden="true"><mba-icon name="user-check" /></span>
        <div class="flex min-w-0 flex-1 flex-col gap-[3px]">
            <span [class]="ui.kicker">Next step</span>
            <b class="font-(family-name:--font-display) text-xl leading-none font-extrabold">{{ heading() }}</b>
            @if (caption()) {
            <span [class]="ui.sub">{{ caption() }}</span>
            }
        </div>
    </div>

    <div class="mx-3 flex flex-col rounded-lg border border-(--border-subtle) bg-(--mba-surface) px-3.5 py-1">
        @if (isShoutPlusNonCash()) {
        <!-- SHOUT+ product, voucher or affiliate: nothing paid in cash. -->
        <div class="flex flex-col gap-1.5 py-3 text-sm text-(--fg-2)">
            <span class="inline-flex items-center gap-2 font-semibold text-(--fg-1)">
                <sp-remuneration-type-icon [type]="resolvedPaymentType()" iconClass="size-5" />
                @switch (resolvedPaymentType()) {
                @case (PaymentMethodTypeEnum.PRODUCT) { SHOUT+ Product }
                @case (PaymentMethodTypeEnum.VOUCHER) { SHOUT+ Voucher }
                @case (PaymentMethodTypeEnum.AFFILIATE) { SHOUT+ Affiliate }
                }
            </span>
            @switch (resolvedPaymentType()) {
            @case (PaymentMethodTypeEnum.PRODUCT) { <p class="m-0">There is no base cash price; the brand provides product directly to the creator.</p> }
            @case (PaymentMethodTypeEnum.VOUCHER) { <p class="m-0">There is no base cash price; the brand pays via a voucher or store credit.</p> }
            @case (PaymentMethodTypeEnum.AFFILIATE) { <p class="m-0">Base price is £0 – the creator earns commission only on tracked affiliate sales.</p> }
            }
        </div>
        } @else {
        <dl class="m-0 flex flex-col divide-y divide-(--border-subtle)">
            @if (isPieceHire()) {
            <!-- Piece-list hire: the server's quote, line by line. Nothing is priced here. -->
            <div class="flex flex-col gap-2 py-3" aria-live="polite" [attr.aria-busy]="pieceQuoteStale()">
                @if (pieceQuote(); as quote) {
                <ul class="m-0 flex list-none flex-col gap-2 p-0 transition-opacity" [class.opacity-50]="pieceQuoteStale()">
                    @for (piece of quote.pieces; track $index) {
                    <li class="flex items-baseline justify-between gap-4 text-sm"><span [class]="lineLabel">{{ quotePieceLabel(piece) }}</span><span [class]="lineValue">{{ toMajor(piece.brand) | currency: currencyCode() }}</span></li>
                    }
                    @for (aud of quote.audience; track $index) {
                    <li class="flex items-baseline justify-between gap-4 text-sm">
                        <span [class]="lineLabel">{{ quoteAudienceLabel(aud) }}@if (aud.levelTitle) { <span class="text-xs text-(--fg-3)"> ({{ aud.levelTitle }})</span>}</span>
                        <span [class]="lineValue">{{ toMajor(aud.brand) | currency: currencyCode() }}</span>
                    </li>
                    }
                    @if (quote.partnerContribution > 0) {
                    <li class="flex items-baseline justify-between gap-4 text-sm"><span [class]="lineLabel">Partner contribution</span><span [class]="lineValue">−{{ toMajor(quote.partnerContribution) | currency: currencyCode() }}</span></li>
                    }
                </ul>
                } @else if (pieceQuoteError(); as error) {
                <p class="m-0 text-sm text-(--danger)" role="alert">{{ error }}</p>
                } @else {
                <span [class]="ui.skeleton + ' w-3/5'" role="status" aria-label="Pricing this hire"></span>
                }
                @if (offeredPieceChannels().length > 0) {
                <div class="flex flex-wrap gap-2 pt-1">
                    @for (channel of offeredPieceChannels(); track channel.id) {
                    <button type="button" [class]="channel.accepted ? ui.btnPrimary : ui.btnOutline" [attr.aria-pressed]="channel.accepted"
                        (click)="togglePieceChannel(channel.id)">
                        @if (channel.accepted) { <mba-icon name="check" size="size-3.5" />{{ channel.label }} } @else { Include {{ channel.label }} }
                    </button>
                    }
                </div>
                <span [class]="ui.sub">Dropping a channel removes it from every video. UGC stays.</span>
                }
                @for (label of unofferedPieceChannelLabels(); track label) {
                <span [class]="ui.sub">{{ label }} was not offered, so it is not on this hire.</span>
                }
                @if (pieceHireAction() === 'nothing-owed') {
                <p [class]="warnText" role="status">{{ NOTHING_OWED_COPY }}.</p>
                }
                @if (piecePriceChanged()) {
                <p [class]="warnText" role="status">The price changed since it was quoted. Check the new total and hire again to confirm.</p>
                }
            </div>
            } @else {
            <div [class]="line"><dt [class]="lineLabel">Base price</dt><dd [class]="lineValue">{{ basePrice() | currency: currencyCode() }}</dd></div>
            @if (videosPerCreator() > 1) {
            <div [class]="line"><dt [class]="lineLabel">No. of videos <span class="text-xs text-(--fg-3)">(price multiplier)</span></dt><dd [class]="lineValue">×{{ videosPerCreator() }}</dd></div>
            }
            @if (socialPlatform() !== null) {
            <div [class]="line"><dt [class]="lineLabel">Social post <span class="text-xs text-(--fg-3)">({{ socialChannelName() }})</span></dt><dd [class]="lineValue">{{ primaryAudiencePrice() | currency: currencyCode() }}</dd></div>
            @if (savingDetails(); as saving) {
            <div class="pb-2.5"><div [class]="winBox" role="status">
                <b>This is a win!</b> You save <b>{{ saving.saving | currency: currencyCode() }}</b> on this hire.
                {{ saving.creatorFirstName }}'s followers exceed your brief's range, so {{ saving.possessivePronoun }} audience bonus was capped at
                <b>{{ saving.cappedTierTitle }}</b> instead of {{ saving.possessivePronoun }} usual <b>{{ saving.usualTierTitle }}</b> tier.
            </div></div>
            } @else if (cappedTierTitle(); as capped) {
            <div class="pb-2.5"><div [class]="winBox" role="status">
                <b>This is a win! This creator usually charges more.</b> They're above the follower range you set for this brief, so their audience bonus has been capped at <b>{{ capped }}</b>.
            </div></div>
            }
            }
            }
            @if (showVat()) {
            <div [class]="line"><dt [class]="lineLabel">VAT</dt><dd [class]="lineValue">{{ vatAmount() | currency: currencyCode() }}</dd></div>
            }
            <div class="flex items-baseline justify-between gap-4 py-3">
                <dt class="font-(family-name:--font-display) text-base font-bold text-(--fg-1)">Total @if (showVat()) {<span class="ml-1 font-(family-name:--font-fallback) text-xs font-medium text-(--fg-3)">VAT included</span>}</dt>
                <dd class="m-0 font-(family-name:--font-display) text-[22px] leading-none font-extrabold tabular-nums text-(--fg-1)" [class.opacity-50]="pieceQuoteStale()">{{ displayTotal() | currency: currencyCode() }}</dd>
            </div>
        </dl>
        }

        <!-- Admin only: is this creator already hired elsewhere? -->
        <ng-container *appHasRole="[4,6]">
            @if (openApplications().length > 0) {
            <div class="mb-3 flex items-center gap-2.5 rounded-lg border-l-4 border-(--warn-dot) bg-(--warn-dot)/10 px-3 py-2.5 text-sm text-(--warn)">
                <mba-icon name="info" size="size-4 shrink-0" />
                <span class="flex-1">Working on <b>{{ openApplications().length }}</b> other live {{ openApplications().length === 1 ? 'brief' : 'briefs' }}.</span>
                <button type="button" class="cursor-pointer font-semibold whitespace-nowrap hover:underline" [attr.aria-expanded]="showOpenApplications()"
                    (click)="showOpenApplications.set(!showOpenApplications())">{{ showOpenApplications() ? 'Hide' : 'Details' }}<span aria-hidden="true"> {{ showOpenApplications() ? '↑' : '→' }}</span></button>
            </div>
            @if (showOpenApplications()) {
            <ul role="list" class="m-0 mb-3 flex list-none flex-col divide-y divide-(--border-subtle) p-0">
                @for (application of openApplications(); track $index) {
                <li class="flex items-center justify-between gap-2 py-2.5">
                    <span class="flex min-w-0 items-center gap-3">
                        <img [src]="imageService.getImageSource(application.brandLogo, 'gallery')" alt="" class="size-9 flex-none rounded-full bg-(--bg-alt)" />
                        <span class="flex min-w-0 flex-col"><b class="truncate text-sm text-(--fg-1)">{{ application.briefName }}</b><span [class]="ui.sub + ' truncate'">{{ application.brandName }}</span></span>
                    </span>
                    <atom-application-status-badge [applicationStatus]="application.applicationStatus"></atom-application-status-badge>
                </li>
                }
            </ul>
            }
            }
        </ng-container>
    </div>

    <!-- Decision -->
    <div class="flex flex-wrap items-center justify-end gap-2 px-3 py-3">
        <button type="button" [class]="ui.btnOutline" [disabled]="disabled() || isHiring()" (click)="decline.emit()">Decline</button>
        @if (isHiring()) {
        <button type="button" [class]="ui.btnPrimary" disabled aria-busy="true">
            <span class="inline-block size-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true"></span>Hiring…
        </button>
        } @else {
        <button type="button" [class]="ui.btnPrimary" [disabled]="disabled() || pieceHireBlocked()" (click)="confirmHire()"
            [attr.title]="pieceHireAction() === 'nothing-owed' ? NOTHING_OWED_COPY : null">
            @if (isShoutPlusNonCash()) {
            <sp-remuneration-type-icon [type]="resolvedPaymentType()" iconClass="size-4" />Hire {{ firstName() }}
            } @else if (pieceHireAction() === 'delivery-only') {
            <mba-icon name="user-check" />Hire for UGC delivery · {{ displayTotal() | currency: currencyCode() }}
            } @else {
            <mba-icon name="user-check" />Hire {{ firstName() }} · {{ displayTotal() | currency: currencyCode() }}
            }
        </button>
        }
    </div>
    @if (!isShoutPlusNonCash()) {
    <p class="m-0 px-4 pb-3.5 text-right {{ ui.sub }}">We only pay the creator once you approve the content. Until then the funds are “reserved for purchase”.</p>
    }
</section>

<!-- Audience bonus: which tier this creator lands in, and why. -->
@if (showAudienceTiers()) {
<section class="flex flex-col gap-2.5">
    <div class="flex flex-col gap-1">
        <h3 [class]="ui.sectionTitle">Audience Bonus</h3>
        <span [class]="ui.sub">Creators are paid more for larger audiences.</span>
    </div>
    <dl class="m-0 flex flex-col divide-y divide-(--border-subtle) overflow-hidden rounded-xl border border-(--border-subtle)">
        @for (level of audienceTiers(); track $index) {
        @let state = tierState(level);
        <div class="flex items-center justify-between gap-4 px-3.5 py-2.5"
            [class]="state === 'applied' ? 'border-l-4 border-(--success) bg-(--success)/10' : state === 'usual' ? 'border-l-4 border-(--app-accent) bg-(--app-accent-soft)' : state === 'outside' ? 'opacity-40' : ''">
            <dt class="flex min-w-0 flex-col gap-0.5">
                <span class="inline-flex flex-wrap items-center gap-2">
                    <b class="text-sm text-(--fg-1)">{{ level.title }}</b>
                    @if (state === 'applied') {
                    <span class="rounded-full bg-(--success)/15 px-2 py-0.5 text-[11px] font-bold text-(--success)">Applied</span>
                    } @else if (state === 'usual') {
                    <span class="rounded-full bg-(--mba-surface) px-2 py-0.5 text-[11px] font-bold text-(--app-accent-strong)">Usual</span>
                    }
                </span>
                <span [class]="ui.sub">{{ formatService.convertIntToString(level.followersFrom) }} – {{ formatService.convertIntToString(level.followersTo) }} followers</span>
            </dt>
            <dd class="m-0 shrink-0 text-sm font-semibold tabular-nums text-(--fg-1)">{{ level.bonusValue / 100 | currency: currencyCode() }}</dd>
        </div>
        }
    </dl>
</section>
}
